import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, Plus, Edit2, Eye, RefreshCw, Trash2, Send, FileText } from 'lucide-react';
import { toast } from 'sonner';

const KINDS = [
  { id: 'lead_nurture', label: 'Lead Nurture' },
  { id: 'welcome', label: 'Welcome' },
  { id: 'transactional', label: 'Transactional' },
];

export default function TemplatesPage() {
  const { api } = useAuth();
  const [kind, setKind] = useState('lead_nurture');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api('get', `/api/admin/templates?kind=${kind}`),
        api('get', '/api/admin/sequences/stats').catch(() => ({ data: null })),
      ]);
      setItems(t.data.items || []);
      if (s.data) setStats(s.data);
    } catch (e) { toast.error('Laden mislukt'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const save = async () => {
    if (!editing.name || !editing.body_html) {
      toast.error('Naam + body vereist');
      return;
    }
    try {
      if (editing.id) {
        await api('patch', `/api/admin/templates/${editing.id}`, editing);
        toast.success('Template bijgewerkt');
      } else {
        await api('post', '/api/admin/templates', editing);
        toast.success('Template aangemaakt');
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Opslaan mislukt'); }
  };

  const archive = async (id) => {
    if (!window.confirm('Template archiveren?')) return;
    await api('post', `/api/admin/templates/${id}/archive`);
    toast.info('Template gearchiveerd');
    load();
  };

  const doPreview = async (id) => {
    try {
      const r = await api('post', `/api/admin/templates/${id}/preview`, {});
      setPreview(r.data);
    } catch (e) { toast.error('Preview mislukt'); }
  };

  const seedDefaults = async () => {
    try {
      const r = await api('post', '/api/admin/sequences/seed-defaults');
      toast.success(`${r.data.seeded} default templates aangemaakt`);
      load();
    } catch (e) { toast.error('Seed mislukt'); }
  };

  const triggerWorker = async () => {
    try {
      const r = await api('post', '/api/admin/sequences/process-now');
      toast.success(`Worker: ${r.data.sent} sent, ${r.data.stopped} stopped, ${r.data.errors} errors`);
      load();
    } catch (e) { toast.error('Worker mislukt'); }
  };

  return (
    <div data-testid="templates-page">
      <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0f172a] flex items-center gap-2"><Mail size={20} className="text-[#D4A017]" /> Email Templates</h1>
          <p className="text-sm text-[#64748b] mt-1">Beheer de email sequence templates voor lead nurturing en transactionele mails.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={triggerWorker} data-testid="trigger-worker-btn" className="text-xs bg-[#0f172a] hover:bg-[#1e1b4b] text-white font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5">
            <Send size={12} /> Verwerk wachtrij nu
          </button>
          <button onClick={seedDefaults} data-testid="seed-defaults-btn" className="text-xs bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
            <RefreshCw size={12} /> Seed defaults
          </button>
          <button onClick={() => setEditing({ kind, step: items.length, name: '', subject_variants: [''], body_html: '<p>Hoi {naam},</p><p>...</p>', status: 'active' })} data-testid="new-template-btn" className="text-xs bg-white border border-slate-200 text-[#0f172a] font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 hover:border-[#D4A017]">
            <Plus size={12} /> Nieuwe template
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Stat label="Active templates" value={stats.active_templates || 0} />
          <Stat label="Leads in sequence" value={stats.by_status?.active || 0} color="#16a34a" />
          <Stat label="Wachtend op verzending" value={stats.due_now || 0} color="#D4A017" />
          <Stat label="Unsubscribed" value={stats.by_status?.unsubscribed || 0} color="#dc2626" />
        </div>
      )}

      {/* Kind switcher */}
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        {KINDS.map(k => (
          <button
            key={k.id}
            data-testid={`kind-tab-${k.id}`}
            onClick={() => setKind(k.id)}
            className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px ${kind === k.id ? 'border-[#D4A017] text-[#0f172a]' : 'border-transparent text-[#94a3b8] hover:text-[#0f172a]'}`}
          >
            {k.label}
          </button>
        ))}
      </div>

      {/* Templates list */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        {loading ? <p className="text-sm text-[#94a3b8] text-center py-8">Laden...</p>
          : items.length === 0 ? <p className="text-sm text-[#94a3b8] text-center py-8">Geen templates. Klik "Seed defaults" voor de standaardset.</p>
          : (
            <div className="space-y-2">
              {items.map(t => (
                <div key={t.id} data-testid={`template-${t.id}`} className="flex justify-between items-center bg-slate-50 rounded-lg p-3 gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.step !== undefined && <span className="bg-[#0f172a] text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase">Stap {t.step}</span>}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{t.status}</span>
                      {t.is_default && <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Default</span>}
                    </div>
                    <p className="font-bold text-sm text-[#0f172a] mt-1">{t.name}</p>
                    <p className="text-xs text-[#64748b]">{(t.subject_variants || [])[0] || ''} · {((t.subject_variants || []).length)} subject varianten</p>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => doPreview(t.id)} data-testid={`preview-${t.id}`} className="bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] text-xs font-semibold p-2 rounded-lg" title="Preview"><Eye size={13} /></button>
                    <button onClick={() => setEditing(t)} data-testid={`edit-${t.id}`} className="bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] text-xs font-semibold p-2 rounded-lg" title="Bewerken"><Edit2 size={13} /></button>
                    <button onClick={() => archive(t.id)} className="bg-white border border-slate-200 hover:border-red-300 text-red-600 text-xs font-semibold p-2 rounded-lg" title="Archiveer"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2"><FileText size={16} /> {editing.id ? 'Template bewerken' : 'Nieuwe template'}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-[#64748b] uppercase tracking-wide">Naam (intern)</label>
                <input data-testid="tpl-name" value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[#64748b] uppercase tracking-wide">Kind</label>
                  <select value={editing.kind || 'lead_nurture'} onChange={e => setEditing({ ...editing, kind: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                    {KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-[#64748b] uppercase tracking-wide">Stap (sequence volgorde)</label>
                  <input type="number" value={editing.step || 0} onChange={e => setEditing({ ...editing, step: parseInt(e.target.value, 10) || 0 })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#64748b] uppercase tracking-wide">Subject varianten (1 per regel — A/B test)</label>
                <textarea data-testid="tpl-subjects" rows={3} value={(editing.subject_variants || []).join('\n')} onChange={e => setEditing({ ...editing, subject_variants: e.target.value.split('\n').filter(x => x.trim()) })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" placeholder="{naam}, even gekeken naar {bedrijf}&#10;Snelle vraag over {bedrijf}" />
                <p className="text-[11px] text-[#94a3b8] mt-1">Tokens: {'{naam}'}, {'{bedrijf}'}, {'{website}'}</p>
              </div>
              <div>
                <label className="text-xs font-bold text-[#64748b] uppercase tracking-wide">Body HTML</label>
                <textarea data-testid="tpl-body" rows={14} value={editing.body_html || ''} onChange={e => setEditing({ ...editing, body_html: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono" />
                <p className="text-[11px] text-[#94a3b8] mt-1">Tokens: {'{naam}'}, {'{bedrijf}'}, {'{website}'}, {'{scan_link}'}, {'{prijzen_link}'}, {'{unsubscribe_link}'}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm font-semibold text-[#64748b]">Annuleren</button>
              <button data-testid="tpl-save" onClick={save} className="bg-[#0f172a] hover:bg-[#1e1b4b] text-white text-sm font-bold px-5 py-2 rounded-lg">Opslaan</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200">
              <p className="text-xs text-[#94a3b8] uppercase font-bold mb-1">Subject</p>
              <p className="font-bold text-[#0f172a]" data-testid="preview-subject">{preview.subject}</p>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="w-full" style={{ height: '600px', border: 'none' }} />
            <div className="p-3 text-right border-t border-slate-200">
              <button onClick={() => setPreview(null)} className="text-sm font-semibold text-[#64748b]">Sluiten</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, color = '#0f172a' }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3">
    <p className="text-2xl font-extrabold" style={{ color }}>{value}</p>
    <p className="text-[10px] text-[#94a3b8] uppercase font-bold tracking-wide mt-1">{label}</p>
  </div>
);
